import React, { useState } from "react";
import { Input } from "src/components";
import { createTeeshotAxiosInstance } from "src/http";
import { useAlert } from "src/hooks";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { useRecoilState } from "recoil";
import { recoilStates } from "src/recoil/recoilStates";
import AlertModal from "src/components/YearBooking/components/common/AlertModal";

interface Step3Props {
  onPrev: () => void;
  formData: any;
  onLoadingChange?: (isLoading: boolean) => void;
}

export default function Step3AdditionalInfo({ onPrev, formData, onLoadingChange }: Step3Props) {
  const { value: groupSnsUrlValue, onChange: onChangeGroupSnsUrl } = Input.hooks.useInput("");
  const { value: groupIntroductionValue, onChange: onChangeGroupIntroduction } =
    Input.hooks.useInput("");
  const { previewTempUrl, handleFileInput, base64 } = Input.hooks.useFileInput();

  const alert = useAlert();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [_, setGroup] = useRecoilState(recoilStates.group);

  // 모달 상태 관리
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChangeGroupIntroduction(e as any);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChangeGroupSnsUrl(e);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return; // 중복 제출 방지

    setIsSubmitting(true);
    onLoadingChange?.(true);
    try {
      const submitData = {
        groupNameValue: String(formData?.groupNameValue ?? "").trim(),
        proposerNameValue: String(formData?.proposerNameValue ?? "").trim(),
        memberCountValue: String(formData?.memberCountValue ?? "").trim(),
        contactNumberValue: String(formData?.contactNumberValue ?? "").trim(),
        groupSnsUrlValue: String(groupSnsUrlValue ?? "").trim(),
        groupIntroductionValue: String(groupIntroductionValue ?? "").trim(),
        activeGroupTypeId: formData?.activeGroupTypeId ?? null,
        activeDistrictId: formData?.activeDistrictId ?? null,
        groupImgBase64: base64,
      };
      console.log("submitData", submitData);
      const res = await createTeeshotAxiosInstance().post("/yearBooking/group", {
        submitData,
      });
      if (res.data.success === false) {
        throw new Error(res.data?.message || "단체 등록 실패");
      }

      const groupResult = await createTeeshotAxiosInstance().get("/yearBooking/group");
      const groupResultData = groupResult?.data?.data;
      if (!groupResultData?.group || !groupResultData?.members) {
        alert({
          content: "그룹정보 조회오류",
          onClose: () => {
            window.location.reload();
          },
        });
        return;
      }
      setGroup({ ...groupResultData.group, members: groupResultData.members });

      // 성공 모달 표시
      setShowSuccessModal(true);
    } catch (error: any) {
      let errorMessage = "단체 등록 중 오류가 발생했습니다.";
      if (error?.code === "ECONNABORTED" || String(error?.message || "").includes("timeout")) {
        errorMessage = "서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해주세요.";
      } else if (error?.response?.status >= 500) {
        errorMessage = "서버 오류가 발생했습니다. 잠시 후 다시 시도해주세요.";
      } else if (error?.response?.data?.message) {
        errorMessage = error.response.data.message;
      }
      // 에러 메시지 설정 및 에러 모달 표시
      setErrorMessage(errorMessage);
      setShowErrorModal(true);
    } finally {
      setIsSubmitting(false);
      onLoadingChange?.(false);
      // navigate는 성공 모달에서 처리하므로 여기서는 제거
    }
  };

  return (
    <StepContainer>
      {/* 성공 모달 */}
      {showSuccessModal && (
        <AlertModal
          onClose={() => setShowSuccessModal(false)}
          title="단체등록 완료"
          subtitle="단체등록이 완료되었습니다."
          lines={["희망하시는 골프장을 3개까지 신청하세요~!"]}
          confirmText="확인"
          onConfirm={() => {
            setShowSuccessModal(false);
            navigate("/year-booking");
          }}
        />
      )}

      {/* 에러 모달 */}
      {showErrorModal && (
        <AlertModal
          onClose={() => setShowErrorModal(false)}
          title="등록 실패"
          subtitle={errorMessage}
          lines={["잠시 후 다시 시도해주세요."]}
          confirmText="확인"
          onConfirm={() => {
            setShowErrorModal(false);
          }}
        />
      )}

      <PageTitle>단체 부가 정보를 입력해주세요.</PageTitle>

      <FormField>
        <FieldLabel>
          <LabelText>단체 카페/밴드 주소</LabelText>
        </FieldLabel>
        <InputField $active={Boolean(groupSnsUrlValue)}>
          <StyledInput
            id="group-sns-url"
            value={groupSnsUrlValue}
            onChange={handleInputChange}
            placeholder="단체 주소를 입력해주세요. (예시: http://cafe.naver.com/)"
          />
        </InputField>
      </FormField>

      <FormField>
        <FieldLabel>
          <LabelText>단체 소개</LabelText>
        </FieldLabel>
        <TextAreaField>
          <StyledTextarea
            id="group-introduction"
            value={groupIntroductionValue}
            onChange={handleTextareaChange}
            placeholder="단체 소개를 입력해주세요. (예시: 사내 골프 동호회입니다.)"
          />
        </TextAreaField>
      </FormField>

      <FormField>
        <FieldLabel>
          <LabelText>단체 대표 로고 및 이미지</LabelText>
        </FieldLabel>
        <UploadContainer>
          <UploadBox>
            {previewTempUrl ? (
              <PreviewImage src={previewTempUrl} alt="로고 미리보기" />
            ) : (
              <UploadIcon>
                <svg
                  width="38"
                  height="38"
                  viewBox="0 0 38 38"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M4.41667 37.75C3.27083 37.75 2.28993 37.342 1.47396 36.526C0.657986 35.7101 0.25 34.7292 0.25 33.5833V4.41667C0.25 3.27083 0.657986 2.28993 1.47396 1.47396C2.28993 0.657986 3.27083 0.25 4.41667 0.25H23.1667C22.4722 1.15278 21.9514 2.14236 21.6042 3.21875C21.2569 4.29514 21.0833 5.38889 21.0833 6.5C21.0833 9.38194 22.099 11.8385 24.1302 13.8698C26.1615 15.901 28.6181 16.9167 31.5 16.9167C32.6111 16.9167 33.7049 16.7431 34.7812 16.3958C35.8576 16.0486 36.8472 15.5278 37.75 14.8333V33.5833C37.75 34.7292 37.342 35.7101 36.526 36.526C35.7101 37.342 34.7292 37.75 33.5833 37.75H4.41667ZM6.5 29.4167H31.5L23.6875 19L17.4375 27.3333L12.75 21.0833L6.5 29.4167ZM29.4167 12.75V8.58333H25.25V4.41667H29.4167V0.25H33.5833V4.41667H37.75V8.58333H33.5833V12.75H29.4167Z"
                    fill="#D1D5D8"
                  />
                </svg>
              </UploadIcon>
            )}
            <HiddenFileInput type="file" onChange={handleFileInput} accept="image/*" />
          </UploadBox>
        </UploadContainer>
      </FormField>

      <ButtonContainer>
        <PrevButton onClick={onPrev}>이전</PrevButton>
        <SubmitButton onClick={handleSubmit} disabled={isSubmitting}>
          {isSubmitting ? "등록 중..." : "등록하기"}
        </SubmitButton>
      </ButtonContainer>
    </StepContainer>
  );
}

const StepContainer = styled.div`
  width: 100%;
`;

const PageTitle = styled.h1`
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 24px;
  height: 24px;
  margin: 0 0 40px 0;
`;

const FormField = styled.div`
  margin-bottom: 24px;
`;

const FieldLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  height: 18px;
`;

const LabelText = styled.span`
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-weight: 600;
  font-size: 14px;
  color: #1a1a1a;
  line-height: 18px;
`;

const InputField = styled.div<{ $active?: boolean }>`
  display: flex;
  width: 100%;
  height: 44px;
  padding: 13px 21px 13px 10px;
  align-items: center;
  border-radius: 6px;
  border: 1px solid ${(p) => (p.$active ? "#007bff" : "#d1d5d8")};
  background: #fdfdfd;
  &:focus-within {
    border-color: #007bff;
  }
`;

const StyledInput = styled.input`
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;

  &::placeholder {
    color: #79818a;
  }
`;

const TextAreaField = styled.div<{ $active?: boolean }>`
  display: flex;
  width: 100%;
  height: 132px;
  padding: 13px 10px;
  align-items: flex-start;
  border-radius: 6px;
  border: 1px solid ${(p) => (p.$active ? "#007bff" : "#d1d5d8")};
  background: #fdfdfd;
  &:focus-within {
    border-color: #007bff;
  }
`;

const StyledTextarea = styled.textarea`
  width: 100%;
  height: 100%;
  border: none;
  outline: none;
  background: transparent;
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  resize: none;

  &::placeholder {
    color: #79818a;
  }
`;

const UploadContainer = styled.div`
  margin-top: 8px;
`;

const UploadBox = styled.div`
  width: 100px;
  height: 100px;
  border: 1px solid #d1d5d8;
  background: #fdfdfd;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &:hover {
    background: #f8f9fa;
  }
`;

const UploadIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
`;

const HiddenFileInput = styled.input`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
`;

const ButtonContainer = styled.div`
  position: fixed;
  bottom: 0;
  left: 50%;
  transform: translateX(-50%);
  padding: 8px;
  background: linear-gradient(
    0deg,
    #fdfdfd 0%,
    #fdfdfd 60%,
    rgba(253, 253, 253, 0.3) 85%,
    rgba(253, 253, 253, 0) 100%
  );
  backdrop-filter: blur(10px);
  z-index: 1000;
  display: flex;
  gap: 12px;
  width: 100%;
  max-width: 800px;
`;

const PrevButton = styled.button`
  flex: 1;
  height: 44px;
  padding: 13px 10px;
  border-radius: 6px;
  border: 1px solid #d1d5d8;
  background: #fdfdfd;
  color: #1a1a1a;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
`;

const SubmitButton = styled.button`
  flex: 1;
  height: 44px;
  padding: 13px 10px;
  border-radius: 6px;
  border: none;
  background: ${(props) => ((props as any).disabled ? "#B0B0B0" : "#007BFF")};
  color: #fdfdfd;
  font-family: Pretendard, -apple-system, Roboto, Helvetica, sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: ${(props) => ((props as any).disabled ? "not-allowed" : "pointer")};
  transition: background-color 0.2s ease;

  &:hover {
    background: ${(props) => ((props as any).disabled ? "#B0B0B0" : "#0056CC")};
  }
`;
